import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import {
	Box,
	Button,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from '@mui/material'
import createSvgIcon from '@mui/material/utils/createSvgIcon'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
	DeploymentPackageRecord,
	ENV_PILL,
	PackageEnv,
	PackageStatus,
	STATUS_PILL,
	getDeploymentPackagePath,
	seedPackages,
} from './packagesMockData'

/** Lucide-style package icon from MetaCroc prototype (`icons.jsx` → package). */
export const PackageIcon = createSvgIcon(
	<>
		<path
			d="m7.5 4.27 9 5.15"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path
			d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path
			d="m3.3 7 8.7 5 8.7-5"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path
			d="M12 22V12"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</>,
	'Package',
)

const Pill = ({
	value,
	map,
}: {
	value: string
	map: Record<string, [string, string]>
}) => {
	const [fg, bg] = map[value] || ['#9A9A9A', '#EDEDED']
	return (
		<Box
			sx={{
				display: 'inline-flex',
				alignItems: 'center',
				gap: '5px',
				px: '8px',
				py: '2px',
				borderRadius: '999px',
				bgcolor: bg,
				color: fg,
				fontSize: '10px',
				fontWeight: 700,
				letterSpacing: '.04em',
				textTransform: 'uppercase',
			}}
		>
			{value}
		</Box>
	)
}

const CreatePackageDialog = ({
	open,
	onClose,
	onCreate,
}: {
	open: boolean
	onClose: () => void
	onCreate: (data: { name: string; env: PackageEnv; description: string }) => void
}) => {
	const [name, setName] = useState('')
	const [env, setEnv] = useState<PackageEnv>('TEST')
	const [description, setDescription] = useState('')
	const close = () => {
		setName('')
		setEnv('TEST')
		setDescription('')
		onClose()
	}
	if (!open) return null
	return (
		<Box
			sx={{
				position: 'fixed',
				inset: 0,
				zIndex: 1300,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				p: '20px',
			}}
		>
			<Box onClick={close} sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.42)' }} />
			<Box
				sx={{
					position: 'relative',
					width: 'min(460px, 96vw)',
					bgcolor: '#fff',
					borderRadius: '14px',
					boxShadow: '0 16px 50px rgba(0,0,0,0.3)',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<Box sx={{ fontSize: 16, fontWeight: 700, color: '#1F4954', px: '24px', pt: '20px', pb: '4px' }}>
					New deployment package
				</Box>
				<Box sx={{ px: '24px', py: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
					<TextField
						size="small"
						fullWidth
						label="Package name"
						value={name}
						autoFocus
						placeholder="SEGMENT_REFRESH_FR"
						onChange={(ev) => setName(ev.target.value)}
						slotProps={{ input: { sx: { borderRadius: '9px', fontSize: '13px' } } }}
					/>
					<FormControl size="small" fullWidth>
						<InputLabel>Target environment</InputLabel>
						<Select
							label="Target environment"
							value={env}
							onChange={(ev) => setEnv(ev.target.value as PackageEnv)}
							sx={{ borderRadius: '9px', fontSize: '13px' }}
						>
							{(['DEV', 'TEST', 'PROD'] as PackageEnv[]).map((e) => (
								<MenuItem key={e} value={e} sx={{ fontSize: 13 }}>
									{e}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<TextField
						size="small"
						fullWidth
						label="Description"
						value={description}
						multiline
						minRows={2}
						onChange={(ev) => setDescription(ev.target.value)}
						slotProps={{ input: { sx: { borderRadius: '9px', fontSize: '13px' } } }}
					/>
				</Box>
				<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', px: '24px', py: '16px' }}>
					<Button onClick={close} sx={{ color: '#6A6A6A', borderRadius: '8px' }}>
						Cancel
					</Button>
					<Button
						variant="contained"
						disableElevation
						disabled={!name.trim()}
						onClick={() => {
							onCreate({ name: name.trim(), env, description: description.trim() })
							close()
						}}
						sx={{ borderRadius: '8px', px: '18px' }}
					>
						Create
					</Button>
				</Box>
			</Box>
		</Box>
	)
}

export const DeploymentPackagesLayout = () => {
	const navigate = useNavigate()
	const { packageId } = useParams<{ packageId?: string }>()
	const [packages, setPackages] = useState(() => seedPackages())
	const [createOpen, setCreateOpen] = useState(false)
	const selected = packages.find((p) => p.id === packageId) || null

	const createPackage = (data: { name: string; env: PackageEnv; description: string }) => {
		const id = `p${Date.now()}`
		const nr: DeploymentPackageRecord = {
			id,
			name: data.name,
			env: data.env,
			status: 'Draft',
			objects: 0,
			author: 'Medilek, Jiri',
			date: new Date().toLocaleDateString('en-GB'),
			description: data.description || 'New curated deployment package.',
			structures: [],
		}
		setPackages((prev) => [nr, ...prev])
		navigate(getDeploymentPackagePath(id))
	}

	if (selected) {
		return (
			<PackageDetail
				pkg={selected}
				onBack={() => navigate(getDeploymentPackagePath())}
				onStatusChange={(status) =>
					setPackages((prev) =>
						prev.map((p) => (p.id === selected.id ? { ...p, status } : p)),
					)
				}
			/>
		)
	}

	return (
		<>
			<PackageList
				packages={packages}
				onOpen={(id) => navigate(getDeploymentPackagePath(id))}
				onCreate={() => setCreateOpen(true)}
			/>
			<CreatePackageDialog
				open={createOpen}
				onClose={() => setCreateOpen(false)}
				onCreate={createPackage}
			/>
		</>
	)
}

const PackageList = ({
	packages,
	onOpen,
	onCreate,
}: {
	packages: DeploymentPackageRecord[]
	onOpen: (id: string) => void
	onCreate: () => void
}) => (
	<Box
		sx={{
			flex: 1,
			minWidth: 0,
			display: 'flex',
			flexDirection: 'column',
			bgcolor: '#F7F7F7',
			height: '100%',
			minHeight: 0,
		}}
	>
		<Box
			sx={{
				px: '26px',
				pt: '20px',
				pb: '16px',
				bgcolor: '#fff',
				borderBottom: '1px solid #E4E4E4',
				display: 'flex',
				alignItems: 'center',
				gap: '12px',
			}}
		>
			<PackageIcon sx={{ fontSize: 20, color: '#1F4954' }} />
			<Box sx={{ flex: 1 }}>
				<Typography sx={{ fontSize: '18px', fontWeight: 800, color: '#1F4954' }}>
					Deployment Packages
				</Typography>
				<Typography sx={{ fontSize: '12px', color: '#9A9A9A' }}>
					Curated change packages ready to deploy across environments
				</Typography>
			</Box>
			<Button
				variant="contained"
				disableElevation
				startIcon={<AddOutlinedIcon sx={{ fontSize: 16 }} />}
				onClick={onCreate}
				sx={{ borderRadius: '9px', px: '16px', height: 38 }}
			>
				New package
			</Button>
		</Box>

		<Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: '18px 26px' }}>
			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: '2fr 90px 110px 90px 1.2fr 100px',
					gap: '12px',
					px: '16px',
					pb: '8px',
					fontSize: '10px',
					fontWeight: 700,
					letterSpacing: '.05em',
					textTransform: 'uppercase',
					color: '#9A9A9A',
				}}
			>
				{['Package', 'Env', 'Status', 'Objects', 'Author', 'Date'].map((h, i) => (
					<Box key={h} sx={{ textAlign: i === 3 ? 'right' : 'left' }}>
						{h}
					</Box>
				))}
			</Box>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
				{packages.map((p) => (
					<Box
						key={p.id}
						onClick={() => onOpen(p.id)}
						sx={{
							display: 'grid',
							gridTemplateColumns: '2fr 90px 110px 90px 1.2fr 100px',
							gap: '12px',
							alignItems: 'center',
							bgcolor: '#fff',
							border: '1px solid #E7E7E7',
							borderRadius: '10px',
							px: '16px',
							py: '12px',
							cursor: 'pointer',
							'&:hover': {
								borderColor: '#1F4954',
								boxShadow: '0 2px 10px rgba(31,73,84,0.08)',
							},
						}}
					>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
							<PackageIcon sx={{ fontSize: 17, color: '#5B43C4' }} />
							<Box sx={{ minWidth: 0 }}>
								<Box
									sx={{
										fontSize: '13.5px',
										fontWeight: 700,
										color: '#1F4954',
										fontFamily: 'ui-monospace, monospace',
										whiteSpace: 'nowrap',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
									}}
								>
									{p.name}
								</Box>
								{p.description ? (
									<Box
										sx={{
											fontSize: '10.5px',
											color: '#9A9A9A',
											whiteSpace: 'nowrap',
											overflow: 'hidden',
											textOverflow: 'ellipsis',
										}}
									>
										{p.description}
									</Box>
								) : null}
							</Box>
						</Box>
						<Pill map={ENV_PILL} value={p.env} />
						<Pill map={STATUS_PILL} value={p.status} />
						<Box sx={{ textAlign: 'right', fontSize: '13px', fontWeight: 700, color: '#1F4954' }}>
							{p.objects}
						</Box>
						<Box sx={{ fontSize: '12.5px', color: '#6A6A6A' }}>{p.author}</Box>
						<Box sx={{ fontSize: '12px', color: '#9A9A9A' }}>{p.date}</Box>
					</Box>
				))}
			</Box>
		</Box>
	</Box>
)

const PackageDetail = ({
	pkg,
	onBack,
	onStatusChange,
}: {
	pkg: DeploymentPackageRecord
	onBack: () => void
	onStatusChange: (status: PackageStatus) => void
}) => (
	<Box
		sx={{
			flex: 1,
			minWidth: 0,
			display: 'flex',
			flexDirection: 'column',
			bgcolor: '#F7F7F7',
			minHeight: 0,
			height: '100%',
		}}
	>
		<Box sx={{ px: '26px', pt: '16px', pb: '16px', bgcolor: '#fff', borderBottom: '1px solid #E4E4E4' }}>
			<Box
				onClick={onBack}
				sx={{
					display: 'inline-flex',
					alignItems: 'center',
					gap: '5px',
					cursor: 'pointer',
					color: '#6A6A6A',
					fontSize: '11.5px',
					fontWeight: 600,
					mb: '10px',
					'&:hover': { color: '#1F4954' },
				}}
			>
				<ArrowBackOutlinedIcon sx={{ fontSize: 13 }} />
				Back to packages
			</Box>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
				<PackageIcon sx={{ fontSize: 22, color: '#1F4954' }} />
				<Typography
					sx={{
						fontSize: '20px',
						fontWeight: 800,
						color: '#1F4954',
						fontFamily: 'ui-monospace, monospace',
					}}
				>
					{pkg.name}
				</Typography>
				<Pill map={ENV_PILL} value={pkg.env} />
				<Pill map={STATUS_PILL} value={pkg.status} />
				<Box sx={{ flex: 1 }} />
				{pkg.status === 'Draft' || pkg.status === 'Ready' ? (
					<Button
						variant="contained"
						disableElevation
						startIcon={<PackageIcon sx={{ fontSize: 16 }} />}
						onClick={() => onStatusChange(pkg.status === 'Draft' ? 'Ready' : 'Deploying')}
						sx={{ borderRadius: '9px', px: '16px', height: 38 }}
					>
						{pkg.status === 'Draft' ? 'Mark ready' : 'Deploy'}
					</Button>
				) : null}
			</Box>
			<Typography sx={{ fontSize: '12px', color: '#9A9A9A', mt: '8px' }}>
				{pkg.author} · {pkg.date} · {pkg.objects} objects
			</Typography>
		</Box>

		<Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: '26px', py: '18px', maxWidth: 980 }}>
			{pkg.description ? (
				<Box
					sx={{
						fontSize: '13px',
						color: '#52666C',
						lineHeight: 1.55,
						mb: '18px',
						p: '12px 14px',
						borderRadius: '10px',
						bgcolor: '#fff',
						border: '1px solid #E8E8E8',
					}}
				>
					{pkg.description}
				</Box>
			) : null}

			<Box
				sx={{
					fontSize: '11px',
					fontWeight: 700,
					letterSpacing: '.05em',
					textTransform: 'uppercase',
					color: '#6A6A6A',
					mb: '9px',
				}}
			>
				Included structures
			</Box>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
				{pkg.structures.length ? (
					pkg.structures.map((s, i) => (
						<Box
							key={`${s.name}-${i}`}
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: '12px',
								bgcolor: '#fff',
								border: '1px solid #E7E7E7',
								borderRadius: '10px',
								px: '14px',
								py: '11px',
							}}
						>
							<TableChartOutlinedIcon sx={{ fontSize: 15, color: '#1F4954' }} />
							<Box sx={{ flex: 1, minWidth: 0 }}>
								<Box sx={{ fontSize: '13px', fontWeight: 600, color: '#2A2A2A' }}>{s.name}</Box>
								<Box
									sx={{
										fontSize: '10px',
										color: '#B0B0B0',
										textTransform: 'uppercase',
										letterSpacing: '.03em',
									}}
								>
									{s.type}
								</Box>
							</Box>
							<Box
								sx={{
									fontSize: '12px',
									fontFamily: 'ui-monospace, monospace',
									fontWeight: 700,
									color: '#1F4954',
								}}
							>
								{s.version}
							</Box>
						</Box>
					))
				) : (
					<Box
						sx={{
							p: '40px',
							textAlign: 'center',
							color: '#B0B0B0',
							fontSize: '13px',
							bgcolor: '#fff',
							border: '1px solid #E7E7E7',
							borderRadius: '10px',
						}}
					>
						No structures in this package yet.
					</Box>
				)}
			</Box>
		</Box>
	</Box>
)
